/**
 * Locations and addresses.
 *
 * A Location is a known place we work at, drive to, or pick up from
 * (warehouses, VariSpace tenants, recurring client sites, suppliers).
 * Addresses can also live inline on a Project for one-off destinations.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, PhoneSchema, UuidSchema } from './common.js';
import { LocationKindSchema } from './enums.js';
// ----------------------------------------------------------------------------
// Address — embedded value object
// ----------------------------------------------------------------------------
export const AddressSchema = z.object({
    line1: z.string().min(1).max(200),
    line2: z.string().nullable().default(null),
    city: z.string().min(1).max(100),
    state: z.string().length(2).describe('US 2-letter state code, e.g. "TX"'),
    postal_code: z.string().min(3).max(20),
    country: z.string().length(2).default('US'),
    latitude: z.number().min(-90).max(90).nullable().default(null),
    longitude: z.number().min(-180).max(180).nullable().default(null),
});
// ----------------------------------------------------------------------------
// Location — first-class registry of known places
// ----------------------------------------------------------------------------
/**
 * Registry address — tolerant variant of AddressSchema. Real registry rows
 * legitimately carry TBD addresses (VariSpace sites pending build-out store
 * empty line1/city/postal), and the API serializer enforces response schemas,
 * so the strict AddressSchema would 500 the whole `GET /locations` list over
 * one TBD row. Project site addresses keep the strict AddressSchema.
 */
export const LocationAddressSchema = AddressSchema.extend({
    line1: z.string().max(200),
    city: z.string().max(100),
    state: z.string().max(2),
    postal_code: z.string().max(20),
});
export const LocationSchema = z.object({
    id: UuidSchema,
    kind: LocationKindSchema,
    name: z.string().min(1).max(150),
    address: LocationAddressSchema,
    contact_name: z.string().nullable(),
    contact_phone: PhoneSchema.nullable(),
    notes: z.string().nullable(),
    is_active: z.boolean().default(true),
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
});
export const LocationCreateSchema = LocationSchema.pick({
    kind: true,
    name: true,
    address: true,
    contact_name: true,
    contact_phone: true,
    notes: true,
});
export const LocationUpdateSchema = LocationCreateSchema.partial();
//# sourceMappingURL=locations.js.map